import React, { useMemo } from "react";
import { CKEditor } from "@ckeditor/ckeditor5-react";
import {
  ClassicEditor,
  Essentials,
  Paragraph,
  Heading,
  Bold,
  Italic,
  Underline,
  Strikethrough,
  Subscript,
  Superscript,
  Code,
  RemoveFormat,
  FontFamily,
  FontSize,
  FontColor,
  FontBackgroundColor,
  Link,
  List,
  TodoList,
  BlockQuote,
  CodeBlock,
  Alignment,
  Indent,
  IndentBlock,
  HorizontalLine,
  Image,
  ImageToolbar,
  ImageCaption,
  ImageStyle,
  ImageResize,
  ImageInsert,
  ImageInsertViaUrl,
  AutoImage,
  LinkImage,
  PictureEditing,
  Base64UploadAdapter,
  Table,
  TableToolbar,
  TableProperties,
  TableCellProperties,
  TableColumnResize,
  TableCaption,
  MediaEmbed,
  PasteFromOffice,
  AutoLink,
  Autoformat,
  TextTransformation,
  FindAndReplace,
  SelectAll,
  SourceEditing,
  SpecialCharacters,
  SpecialCharactersEssentials,
  Highlight,
  GeneralHtmlSupport,
  WordCount,
  Emoji,
  Mention,
  HtmlEmbed,
  ShowBlocks,
  Style,
  ListProperties,
  Bookmark,
  AccessibilityHelp,
} from "ckeditor5";

import "ckeditor5/ckeditor5.css";

// Drop-in rich text editor (replaces Quill). Images are embedded inline as
// base64 via Base64UploadAdapter — including drag-and-drop and paste — so no
// upload endpoint is required.
const RichTextEditor = ({ value, onChange, placeholder }) => {
  const config = useMemo(
    () => ({
      licenseKey: "GPL",
      plugins: [
        Essentials,
        Paragraph,
        Heading,
        Bold,
        Italic,
        Underline,
        Strikethrough,
        Subscript,
        Superscript,
        Code,
        RemoveFormat,
        FontFamily,
        FontSize,
        FontColor,
        FontBackgroundColor,
        Link,
        AutoLink,
        List,
        TodoList,
        BlockQuote,
        CodeBlock,
        Alignment,
        Indent,
        IndentBlock,
        HorizontalLine,
        Image,
        ImageToolbar,
        ImageCaption,
        ImageStyle,
        ImageResize,
        ImageInsert,
        ImageInsertViaUrl,
        AutoImage,
        LinkImage,
        PictureEditing,
        Base64UploadAdapter,
        Table,
        TableToolbar,
        TableProperties,
        TableCellProperties,
        TableColumnResize,
        TableCaption,
        MediaEmbed,
        PasteFromOffice,
        Autoformat,
        TextTransformation,
        FindAndReplace,
        SelectAll,
        SourceEditing,
        SpecialCharacters,
        SpecialCharactersEssentials,
        Highlight,
        GeneralHtmlSupport,
        WordCount,
        Mention,
        Emoji,
        HtmlEmbed,
        ShowBlocks,
        Style,
        ListProperties,
        Bookmark,
        AccessibilityHelp,
      ],
      htmlSupport: {
        allow: [{ name: /.*/, attributes: true, classes: true, styles: true }],
      },
      list: {
        properties: { styles: true, startIndex: true, reversed: true },
      },
      style: {
        definitions: [
          { name: "Lead paragraph", element: "p", classes: ["lead"] },
          { name: "Info box", element: "p", classes: ["info-box"] },
          { name: "Marker", element: "span", classes: ["marker"] },
          { name: "Spoiler", element: "span", classes: ["spoiler"] },
        ],
      },
      toolbar: {
        items: [
          "undo",
          "redo",
          "|",
          "findAndReplace",
          "selectAll",
          "|",
          "heading",
          "style",
          "|",
          "fontFamily",
          "fontSize",
          "fontColor",
          "fontBackgroundColor",
          "highlight",
          "|",
          "bold",
          "italic",
          "underline",
          "strikethrough",
          "subscript",
          "superscript",
          "code",
          "removeFormat",
          "|",
          "specialCharacters",
          "emoji",
          "link",
          "bookmark",
          "blockQuote",
          "codeBlock",
          "htmlEmbed",
          "|",
          "bulletedList",
          "numberedList",
          "todoList",
          "outdent",
          "indent",
          "alignment",
          "|",
          "insertImage",
          "insertTable",
          "mediaEmbed",
          "horizontalLine",
          "|",
          "sourceEditing",
          "showBlocks",
        ],
        shouldNotGroupWhenFull: true,
      },
      heading: {
        options: [
          { model: "paragraph", title: "Paragraph", class: "ck-heading_paragraph" },
          { model: "heading1", view: "h1", title: "Heading 1", class: "ck-heading_heading1" },
          { model: "heading2", view: "h2", title: "Heading 2", class: "ck-heading_heading2" },
          { model: "heading3", view: "h3", title: "Heading 3", class: "ck-heading_heading3" },
          { model: "heading4", view: "h4", title: "Heading 4", class: "ck-heading_heading4" },
        ],
      },
      image: {
        toolbar: [
          "imageStyle:inline",
          "imageStyle:block",
          "imageStyle:side",
          "|",
          "toggleImageCaption",
          "imageTextAlternative",
          "linkImage",
          "|",
          "resizeImage",
        ],
      },
      table: {
        contentToolbar: [
          "tableColumn",
          "tableRow",
          "mergeTableCells",
          "tableProperties",
          "tableCellProperties",
          "toggleTableCaption",
        ],
      },
      link: {
        addTargetToExternalLinks: true,
        defaultProtocol: "https://",
      },
      placeholder: placeholder || "Write your story…",
    }),
    [placeholder]
  );

  return (
    <div className="iw-ckeditor">
      <style>{`
        .iw-ckeditor .ck-editor__editable { min-height: 340px; max-height: 80vh; }
        .iw-ckeditor .ck.ck-editor__main > .ck-editor__editable { background: transparent; }
        /* Remove the box border CKEditor wraps the toolbar in; keep only a
           full-width bottom divider under the toolbar. */
        .iw-ckeditor .ck.ck-editor__top .ck-sticky-panel__content {
          border: none;
          border-bottom: 1px solid #e5e7eb;
        }
        .iw-ckeditor .ck.ck-toolbar {
          border: none;
          border-radius: 0;
          background: transparent;
        }
        /* Drop CKEditor's own editable border + blue focus ring; the wrapper
           provides the single field-style border (gray → red on focus). */
        .iw-ckeditor .ck.ck-editor__editable_inline { border: none; }
        .iw-ckeditor .ck.ck-editor__editable.ck-focused {
          border: none;
          box-shadow: none;
        }
        /* The app's global Tailwind preflight flattens headings; restore the
           sizes inside the editor so authoring matches the public page. */
        .iw-ckeditor .ck-content h1 { font-size: 2.25rem; font-weight: 700; line-height: 1.2; }
        .iw-ckeditor .ck-content h2 { font-size: 1.875rem; font-weight: 700; line-height: 1.25; }
        .iw-ckeditor .ck-content h3 { font-size: 1.5rem; font-weight: 600; line-height: 1.3; }
        .iw-ckeditor .ck-content h4 { font-size: 1.25rem; font-weight: 600; line-height: 1.35; }
        .iw-ckeditor .ck-content p { margin: 0.75rem 0; }
        /* Tailwind preflight zeroes list padding, pushing bullets/numbers
           outside the editor; restore indentation so markers stay inside. */
        .iw-ckeditor .ck-content ul,
        .iw-ckeditor .ck-content ol { padding-left: 1.5rem; }
      `}</style>
      <CKEditor
        editor={ClassicEditor}
        config={config}
        data={value || ""}
        onChange={(_event, editor) => onChange?.(editor.getData())}
      />
    </div>
  );
};

export default RichTextEditor;
